/**
 * Grading-scheme REFINE — bringing an existing scheme back in line with a master solution that changed.
 *
 * The third producer of `gradingScheme`, and the one the other two cannot cover. Extraction reads an
 * uploaded scheme; generation writes one where none exists (`rootNeedsGsWork` only picks up roots with no
 * scheme at all). A task whose master solution was just corrected has a scheme — it is simply written
 * against the old solution — so generation declines it and the stale scheme survives. Only a refine changes
 * a scheme that is already there.
 *
 * WHY IT IS A JOB. It is dispatched when the master-solution step completes and reports which tasks its content
 * actually changed. The first version of that did the refine inline in the transition hook, as a
 * detached promise — LLM work, minutes long, outside the queue: no lease, no retry, no LLM gate, no
 * cancellation, and gone entirely if the process restarted mid-call. Everything the job system exists to
 * give a long-running model call, this was outside of. As a job it gets all of it, and the hook goes back to
 * doing the only thing a hook should: deciding what to release next.
 *
 * IT JOINS THE GRADING-SCHEME STEP rather than reporting separately, and that is what makes the rest of the
 * chain fall out for free. `gsRefine` is one of the `gradingScheme` types in `STEP_JOB_TYPES`, and the
 * dispatcher opens a `gradingScheme` run before enqueuing these — so when the last of them finishes and no
 * job of that step is left live, the ordinary `gradingSchemeStep` declaration runs and announces the step.
 *
 * THE RE-GRADE IS THIS ROW'S OWN, though, not the step's: whoever asked for the refine attached the scope it wants
 * re-graded (`regrade`), and `dispatchOwnRegrade` below acts on it when this row completes. The step keeps only the
 * case no row asked about — an uploaded or generated scheme, which has no requester to attach anything.
 *
 * NOT DERIVABLE, which is why nothing reconciles it into existence: "this scheme is stale relative to its
 * master solution" cannot be read off the exam. The two are free text, and comparing them is the model call
 * this job IS.
 */

import { logger } from '@adornis/base/logging.js';
import { refineGradingSchemeAgent, refineLanguageUseCriteriaAgent } from '../../agents/gs-generation-agent.js';
import { gradingSchemeStep } from '../steps/grading-scheme.step.js';
import * as conditions from '../job-conditions.js';
import { taskTreeLockKey, type JobTransition } from '../job.js';
import type { RefineRequest, RegradeIntent } from '../refine.js';
import { defineJob } from '../registry.js';

export interface GsRefinePayload {
  /** Main task numbers whose scheme is stale. What the refine agent addresses tasks by. */
  taskRefs: string[];
  /** Why, in the words handed to the model. */
  feedback: string;
  /**
   * WHICH FAMILY to refine — `content` (the default) or `grammar`. Two independent engines behind one type:
   * content lives on `GradingScheme.criteria`, grammar always on a `LanguageUseScheme` in `exam.languageUseSchemeRules` —
   * solo (one `dependsOn` entry) or multi-member, which is a difference in how it is GRADED, not in where it lives.
   *
   * ONE TYPE RATHER THAN TWO, because everything a second type would have wanted is already shared: the same
   * step, the same lock, the same `ready()` gates, the same run. What differs is one agent call, so it is one
   * branch in the handler.
   *
   * `both` is deliberately NOT a value. The two families need separately-scoped re-grades — a grammar-rule
   * edit must not rewrite content grades, and vice versa — and a re-grade's scope travels as the row's own
   * `regrade` intent. One row cannot carry two. A caller wanting both enqueues two rows.
   */
  target?: 'content' | 'languageUse';
  /**
   * Scopes a `languageUse` refine to ONE LanguageUseScheme — a task can belong to more than one, so without
   * this, refining a task also refines every OTHER scheme it happens to share that task with.
   * `groupNumber` (the teacher/chat-facing "Gruppe N") is preferred; `languageUseSchemeId` is the internal
   * fallback for a legacy scheme with no `displayNumber`. Ignored for `content`.
   */
  groupNumber?: number;
  languageUseSchemeId?: string;
  /**
   * The re-grade this refine should set off once it lands — see `RegradeIntent` and `dispatchOwnRegrade`.
   *
   * On the ROW rather than on the progress record because one run can hold several refines with different scopes,
   * and the record has room for one. Never read by the handler below — only by the transition hook, so that a
   * failed attempt keeps it and a retry that succeeds still dispatches it.
   */
  regrade?: RegradeIntent;
  [key: string]: unknown;
}

/**
 * The re-grade this refine was asked to set off — dispatched by the ROW ITSELF, from its own payload.
 *
 * THIS IS WHY NOTHING HARVESTS INTENTS ANY MORE. The step's closure used to go looking for the `regrade` intents of
 * every refine in the run and act on them, which needed a `harvested` flag on each row to make the claim
 * once-only — a closure fires per terminal transition and must not double-dispatch. A row acting on its OWN payload
 * needs none of that: it is reached exactly once per completion, and if it is reached twice the grading `dedupeKey`
 * and the ledger's `$setUnion` are both idempotent.
 *
 * ONLY ON `done`. A refine that failed changed no scheme, so there is nothing to re-grade against — and the row
 * keeps its intent, so a retry that succeeds still dispatches it.
 *
 * SCOPED TO THE ROW'S OWN `taskRefs` rather than to what the run reports as changed, so a subtask-level insight
 * stays a subtask-level re-grade instead of widening to the main task a diff reports.
 */
const dispatchOwnRegrade = async (event: JobTransition<GsRefinePayload>): Promise<void> => {
  if (event.to !== 'done') return;
  const intent = event.job.payload.regrade;
  if (!intent) return;

  const examId = event.job.examId;
  const { dispatchRegradeIfGrading } = await import('../../pipeline/regrade.js');
  logger.info({ examId, taskRefs: event.job.payload.taskRefs, cause: intent.cause }, 'Refine landed — re-grading');
  await dispatchRegradeIfGrading({
    examId,
    causes: [intent.cause],
    taskRefs: event.job.payload.taskRefs,
    ...(intent.target ? { target: intent.target } : {}),
    // Scopes the re-grade to the ONE scheme this refine actually touched — the row's own taskRefs are
    // that scheme's dependsOn, which can include a task that ALSO belongs to another, untouched scheme;
    // without this the re-grade would re-derive "every scheme covering these tasks" and sweep that one
    // in too. Absent for a content refine or an unscoped languageUse refine (multiple distinct schemes
    // legitimately touched via separate, unambiguous task refs) — see GsRefinePayload.languageUseSchemeId.
    ...(event.job.payload.languageUseSchemeId ? { languageUseSchemeId: event.job.payload.languageUseSchemeId } : {}),
    ...(intent.submissionIds?.length ? { submissionIds: intent.submissionIds } : {}),
    ...(typeof intent.previouslyApprovedCount === 'number'
      ? { previouslyApprovedCount: intent.previouslyApprovedCount }
      : {}),
  });
};

export const gradingSchemeRefineJob = defineJob<GsRefinePayload>({
  type: 'gsRefine',

  /**
   * PER ROW, and it must run before the step's own completion — which it does, because `emitJobTransition` awaits
   * `onTransition` first. That order is load-bearing: `gradingSchemeStep`'s follow-up asks whether the tasks it is
   * about to re-grade are already spoken for (`queuedRegradeRefs`), and the answer is what this dispatch writes.
   */
  onTransition: dispatchOwnRegrade,

  /**
   * `reupload: false` — a refine is a correction to this artifact, not a fresh delivery of it, and the announcement
   * words the two differently. The per-pass fields are NOT cleared from here — `startSolutionProgress` does that, in
   * the write that declares the record IN_PROGRESS, which for a refine is inside the agent. Clearing them at dispatch
   * wiped the sets of whatever pass was still running on this shared record.
   *
   * NO `open`: it deliberately writes neither the status nor the done set, and the two restrictions have the same
   * cause. `producer` is what `artifactExtractionRunning` reads to decide whether a refine may run beside an upload, so
   * writing it here would overwrite the very state this row's own gate is about to consult — and a status write without
   * it is worse rather than safer: on an artifact whose content came from an upload the producer is still
   * `extraction`, so setting IN_PROGRESS at dispatch would make the refine's gate see an extraction in flight and defer
   * the row indefinitely. Which leaves the done set, and it has to move WITH the status or not at all: pulling the
   * units here while the record still said COMPLETED is what displayed a finished artifact at 0 of 4, a bar that had
   * gone backwards with nothing to explain it, minutes before the job that would say IN_PROGRESS was claimed. The
   * refine agents do both together (`dropSolutionTasks` then `startSolutionProgress`), which is the only place that
   * knows the work has actually begun.
   */
  onStepStart: { reupload: false },

  /**
   * One row carrying every ref — see `ms-refine.job.ts` for the whole of why, including what opening the run must not
   * touch. What differs here is `target` and the `regrade` intent, both of which ride the payload.
   */
  batch: {
    units: (_examId, scope) => {
      const { taskRefs = [], feedback = '', target, groupNumber, languageUseSchemeId, regrade } = scope as Partial<RefineRequest>;
      if (!taskRefs.length) return [];
      return [
        {
          taskRefs,
          feedback,
          // PART OF THE DEDUPE KEY, unlike the intent below — a content and a languageUse refine of the same tasks are
          // two pieces of work. The intent deliberately is not: two refines of the same refs are one piece.
          ...(target ? { target } : {}),
          ...(typeof groupNumber === 'number' ? { groupNumber } : {}),
          ...(languageUseSchemeId ? { languageUseSchemeId } : {}),
          ...(regrade ? { regrade } : {}),
        },
      ];
    },
    joinTwin: true,
    /** Generous, because the caller is a teacher who asked for this specific change and wants to hear that it landed. */
    waitMs: 20 * 60_000,
  },

  /** Same step as the extraction and the generation, so a refine mid-extraction holds the step open. */
  onStepComplete: gradingSchemeStep,

  /**
   * The grading-scheme artifact key, NOT a per-task one.
   *
   * Unlike generation, a refine is enqueued as ONE job carrying several task refs, because the agent takes
   * the set and reasons across it. There is no per-root split to lock at, and sharing the artifact key is
   * what keeps it from running beside an extraction of the same field.
   */
  lockKey: examId => `${taskTreeLockKey(examId)}:gradingScheme`,

  // Per ref set AND per family: two different master-solution changes can leave two disjoint sets of schemes
  // stale, and both refines have to happen. Sorted so ordering cannot produce two rows for one set.
  //
  // `target` IS PART OF THE KEY, and leaving it out is the bug this line exists to prevent: a content and a
  // languageUse refine of the same tasks are two pieces of work, and without the family in the key the second
  // insert bounces off the first. `enqueueForType` returns `null` for a dedupe — an ordinary return value — so
  // the loss would be silent, and the vanished row would take its `regrade` intent with it.
  // `groupNumber`/`languageUseSchemeId` are part of the key too, for the same reason `target` is: a
  // scheme-scoped languageUse refine and an unscoped one over the same refs are different work, and without
  // this the second insert would bounce off the first.
  dedupeKey: (examId, payload) =>
    `gsRefine:${examId}:${payload.target ?? 'content'}:${payload.groupNumber ?? ''}:${payload.languageUseSchemeId ?? ''}:${[...payload.taskRefs].sort().join(',')}`,

  ready: (exam, job) => {
    if (!conditions.hasTasks(exam)) return { verdict: 'wait', because: 'gsRefine:no-tasks' };
    if (job.payload.taskRefs.length === 0) return { verdict: 'drop', because: 'gsRefine:no-tasks-named' };
    // An extraction of this artifact is a teacher uploading a scheme, which supersedes refining the old one.
    if (conditions.artifactExtractionRunning(exam, 'gradingScheme')) {
      return { verdict: 'wait', because: 'gsRefine:extraction-running' };
    }
    // CONTENT ONLY. Refining content reads the master solution it is aligning to, so it must not run while one
    // is being written. A languageUse refine reads the task's own POINTS and the existing grammar rules — never the
    // master solution — so holding it back for an upload it does not consult would only make it slower.
    if (job.payload.target !== 'languageUse' && conditions.artifactExtractionRunning(exam, 'masterSolution')) {
      return { verdict: 'wait', because: 'gsRefine:master-solution-changing' };
    }
    return 'run';
  },

  maxRuntimeMs: 15 * 60_000,
  maxAttempts: 2,

  handler: async (examId, payload) => {
    // THE RE-GRADE IS NOT DISPATCHED ON EITHER BRANCH. Each branch's write is recorded — content through
    // `persistTaskArtifact`, grammar by writing `tasks` / `languageUseSchemeRules` — and `dispatchOwnRegrade` above
    // acts on the row's `regrade` intent once this returns successfully. Keeping it out of the handler is what
    // makes the intent survive a retry: the payload still carries it.
    if (payload.target === 'languageUse') {
      logger.info({ examId, taskRefs: payload.taskRefs }, 'Refining grammar criteria');
      const grammar = await refineLanguageUseCriteriaAgent(examId, payload.taskRefs, payload.feedback, {
        groupNumber: payload.groupNumber,
        languageUseSchemeId: payload.languageUseSchemeId,
      });
      if (!grammar.success) throw new Error(grammar.error ?? 'Grammar criteria refine reported a failure');

      // ONE LIST, because there is only one kind of grammar rule now. This used to union `refinedOwnTaskRefs` with
      // `refinedGroupTaskRefs` — a task's own merged criteria versus a shared group — and that distinction is gone:
      // every rule is a LanguageUseScheme, solo (one `dependsOn` entry) or multi-member, so the agent reports one set.
      const refined = [...new Set(grammar.refinedGroupTaskRefs)];
      logger.info({ examId, refined, blocked: grammar.blockedTaskRefs }, 'Grammar criteria refined');
      if (!grammar.blockedTaskRefs.length) return { note: `refined grammar for ${refined.join(', ')}` };

      // THE ONE THING THE CALLER CANNOT DERIVE, handed back as the row's note — see `RefineWaitResult`. A task
      // the teacher NAMED can be impossible to give a rule to: this agent also BACKFILLS rules for requested
      // tasks that have none, and it cannot compute a grammar share for a task whose content scheme has no
      // points yet (`blockedNoPoints`). That is a message for the teacher, not a failure — the other named
      // tasks were refined — so it rides the successful outcome rather than throwing.
      //
      // The note is the FINISHED SENTENCE rather than the raw refs, so the tool appends it verbatim. A tool
      // parsing a diagnostic string back into data would be a format contract nothing enforces, and the words
      // belong next to the facts that produced them.
      return {
        note:
          `HINWEIS: Für folgende Aufgabe(n) konnte noch keine Grammatik-Regel erstellt werden, da noch keine ` +
          `Bewertungskriterien mit Punkten vorliegen: ${grammar.blockedTaskRefs.join(', ')}. Informiere den ` +
          `Nutzer, dass dafür zuerst das inhaltliche Bewertungsschema erstellt werden muss.`,
      };
    }

    logger.info({ examId, taskRefs: payload.taskRefs }, 'Refining grading scheme against a changed master solution');
    const result = await refineGradingSchemeAgent(examId, payload.taskRefs, payload.feedback);
    if (!result.success) throw new Error(result.error ?? 'Grading scheme refine reported a failure');
    return { note: `refined grading scheme for ${payload.taskRefs.join(', ')}` };
  },
});
